import React, {
  useContext, createContext, useState, useEffect, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

import { isProduction } from '../../utils/environmentUtils';
import { getActiveExperimentFromQueryParameter } from '../../utils/googleOptimize/queryParameter';
import { persistOptimizeActiveExperiments, persistExperiments, clearPersistedExperiments } from '../../utils/googleOptimize/persistActiveExperiments';

const ExperimentsWidget = dynamic(import('./components/ExperimentsWidget'), { ssr: false });

export const ExperimentsContext = createContext({ experiments: {} });

export const useExperimentsList = () => {
  const { experiments } = useContext(ExperimentsContext);
  return experiments;
};

export const useExperimentInfo = (experimentId, allowedRoutes) => {
  const { pathname } = useRouter();
  const isAllowedRoute = allowedRoutes ? allowedRoutes.includes(pathname) : true;

  const { experiments } = useContext(ExperimentsContext);

  return isAllowedRoute && experiments[experimentId]
    ? experiments[experimentId]
    : { variant: null };
};

export const ExperimentsContextProvider = ({
  value,
  availableExperiments,
  children,
}) => {
  const [contextValue, setContextValue] = useState({ ...value });

  const saveExperimentsOnContext = useCallback((variant, experimentId) => {
    setContextValue(prevContextValue => ({
      ...prevContextValue,
      experiments: {
        ...prevContextValue.experiments,
        [experimentId]: { variant },
      },
    }));

    persistExperiments(variant, experimentId);
  }, []);

  const resetExperimentsOnContext = () => {
    setContextValue({ ...contextValue, experiments: {} });
    clearPersistedExperiments();
  };

  useEffect(() => {
    const activeExperimentFromQueryParameter = getActiveExperimentFromQueryParameter();
    if (!activeExperimentFromQueryParameter) return;

    const { experimentId, variant } = activeExperimentFromQueryParameter;
    persistExperiments(variant, experimentId);
  }, []);

  useEffect(() => {
    persistOptimizeActiveExperiments(saveExperimentsOnContext);
  }, []);

  return (
    <ExperimentsContext.Provider value={{ ...value, ...contextValue }}>
      {children}
      {!isProduction()
        && availableExperiments.length > 0
        && (
          <ExperimentsWidget
            activeExperiments={contextValue.experiments}
            availableExperiments={availableExperiments}
            saveExperimentsOnContext={saveExperimentsOnContext}
            resetExperimentsOnContext={resetExperimentsOnContext}
          />
        )}
    </ExperimentsContext.Provider>
  );
};

ExperimentsContextProvider.propTypes = {
  availableExperiments: PropTypes.arrayOf(PropTypes.object),
  children: PropTypes.node.isRequired,
  value: PropTypes.shape({
    experiments: PropTypes.objectOf(
      PropTypes.shape({ variant: PropTypes.string }),
    ),
  }),
};

ExperimentsContextProvider.defaultProps = {
  availableExperiments: [],
  value: { experiments: {} },
};
